import { Component, HostListener, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { InteractionService } from '../../service/interaction/interaction.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { ToastrService } from 'ngx-toastr';
import { ConstantConfig } from '../../utility/constant-config';
import { UserProfile } from '../../model/rolePlay.model';
import { ChatService } from '../../service/chat-service';
import { ProfileInfoComponent } from '../profile-info/profile-info.component';
import { FormBuilder, FormGroup } from '@angular/forms';
import { PlatformRegionService } from '../../../app/service/shared/platform-region.service';
import { KeyVaultService } from '../../service/key-vault.service';
import { TraineeListService } from '../../service/trainee-list.service';
import { TextToSpeechService } from '../../service/text-to-speech/text-to-speech.service';
import { UserApiService } from '../../v2/apis/core/user-api.service';
import { RoleStateService } from '../../v2/services/core/role-state.service';
import { UnsavedChangesService } from '../../v2/services/unsaved-changes.service';
import { UnsavedChangesDialogService } from '../../v2/services/unsaved-changes-dialog.service';

type ProfileType = {
  givenName?: string,
  surname?: string,
  userPrincipalName?: string,
  id?: string,
  displayName?: string
}

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
    standalone: false
})

export class HeaderComponent implements OnInit {
  private readonly adminRoleLabel = 'ITADMINUSER';
  switchTrainee: boolean = true;
  config: any = ConstantConfig;
  name: string = '';
  @Input() title: string = '';
  givenName: string = "";
  displayName: string = "";
  displayRole: string = "";
  loginDisplay = false;
  role: string = 'TRAINEE';
  profile!: ProfileType;
  hideHeader: boolean = true;
  userId: number = 0;
  userProfile!: UserProfile;
  isDropdownVisible = false;
  isProfileHighlighted = false;
  hideProfile = true;
  roles: any[] = [];
  menuForm: FormGroup;
  dropdownOptions: any;
  rolesList: any;
  showDropdown: boolean;
  showRole: boolean;
  productLabel: string;
  accessToken: any;
  profileImageUrl: string;
  isExistProfileImage: boolean = false;
  apimUrl: string;
  userGuideFileName: string = '';
  private readonly boundCloseDropdown = this.closeDropdown.bind(this);

  constructor(public Router: Router, private intService: InteractionService, private userApiService: UserApiService,
    private platformRegionService: PlatformRegionService, private dialog: MatDialog, private toastr: ToastrService,
    public chatService: ChatService, public fb: FormBuilder, private keyVaultService: KeyVaultService,
    private traineeService: TraineeListService, private readonly tts: TextToSpeechService, private roleStateService: RoleStateService,
    private unsavedChangesService: UnsavedChangesService, private unsavedChangesDialogService: UnsavedChangesDialogService) {
    this.intService.refreshHeader$.subscribe((value) => {
      if (value === 'platformchange')
        this.ngOnInit();
    });
    this.intService.labelSubject.subscribe(label => {
      this.productLabel = label;
    });
  }

  ngOnInit(): void {
    this.getProfileImage();
    this.checkMobileDevice();
    this.initializeApimUrl();
    this.menuForm = this.fb.group({
      roleName: ['']
    });
    this.showDropdown = false;
    this.loadRoleDropdownOptions();
    document.addEventListener('click', this.boundCloseDropdown);
    this.displayName = sessionStorage.getItem("displayName");
    this.givenName = sessionStorage.getItem("givenName");
    this.role = sessionStorage.getItem("role");
    this.displayRole = sessionStorage.getItem("displayRole");
    this.intService.hideComponentObservable$.subscribe((value: any) => {
      this.hideHeader = !value.hideHeader;
    });
    if (this.Router.url === "/app-product-list" || this.Router.url == '/app-unauthorizeduser') {
      this.hideProfile = true;
    } else {
      this.hideProfile = false;
    }
  }

  getProfileImage() {
    this.userApiService.getProfileImage().subscribe({
      next: (blob) => {
        const reader = new FileReader();
        reader.onload = () => {
          sessionStorage.setItem('profileImageUrl', reader.result as string);
          this.profileImageUrl = reader.result as string;
          this.isExistProfileImage = !!this.profileImageUrl;
        };
        reader.readAsDataURL(blob);
      },
      error: (error) => {
        this.isExistProfileImage = false;
        console.error('Error fetching photo:', error);
        if (error.error) {
          console.log('Error details:', error.error);
        }
      }
    });
  }

  capitalizeFirstLetter(text: string): string {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  profileInfo() {
    this.dialog.open(ProfileInfoComponent, {
      width: '670px'
    });
  }
  getFontSize(label?: string | null): number {
    const length = (label ?? '').length;
    const width = window.innerWidth;
    if (width <= 768) {
      return length > 15 ? 12 : 18;
    } else {
      return 24;
    }
  }

  // Collect the context required by both role loading and role switching flows.
  private getRoleRequestContext(): { businessUnitCode: string; regionName?: string } {
    const businessUnitCode = sessionStorage.getItem('businessUnitCode') || '';
    const region = this.platformRegionService.selectedRegion;
    const regionName = typeof region === 'string' ? region : region?.name;
    return { businessUnitCode, regionName };
  }

  // The API can return region data in different shapes depending on the source.
  private getRoleRegion(role: any): string | undefined {
    if (typeof role.region_name === 'string' && role.region_name) return role.region_name;
    if (typeof role.region === 'string' && role.region) return role.region;
    if (role.region && typeof role.region === 'object' && role.region.name) return role.region.name;
    if (typeof role.user_default_region === 'string' && role.user_default_region) return role.user_default_region;
    return undefined;
  }

  private normalizeRoleLabel(roleLabel?: string | null): string {
    return String(roleLabel || '').trim().toUpperCase();
  }

  private isAdminRole(roleLabel?: string | null): boolean {
    return this.normalizeRoleLabel(roleLabel) === this.adminRoleLabel;
  }

  private getSelectedRoleLabel(): string {
    return String(
      this.platformRegionService.selectedRole?.role_label
      || sessionStorage.getItem('displayRole')
      || ''
    );
  }

  private shouldShowRoleDropdown(_selectedRoleCode?: string): boolean {
    return this.dropdownOptions.length > 0 && this.isAdminRole(this.getSelectedRoleLabel());
  }

  // Keep only roles that belong to the active business unit and region.
  private getMatchedRoles(roles: any[], businessUnitCode: string, regionName?: string): any[] {
    let matchedRoles = roles.filter(role => (role.business_unit_code || '') === businessUnitCode);
    if (regionName) {
      matchedRoles = matchedRoles.filter(role => (this.getRoleRegion(role) || '') === regionName);
    }
    return matchedRoles;
  }

  // Preserve backend priority as display order, but do not hide any matched roles.
  private toDropdownOptions(roles: any[]): { label: string; value: string }[] {
    return roles
      .slice()
      .sort((a, b) => {
        const aPriority = a.role_priority ?? Number.MAX_SAFE_INTEGER;
        const bPriority = b.role_priority ?? Number.MAX_SAFE_INTEGER;
        return aPriority - bPriority;
      })
      .map(role => ({
        label: String(role.role_label || role.role_name || role.role_code || ''),
        value: String(role.role_code || '')
      }));
  }

  // Reuse persisted selection when possible; otherwise fall back to the first valid role.
  private resolveSelectedRoleCode(roles: any[], fallbackRoleCode: string): string {
    const persistedRoleCode = this.roleStateService.getSnapshot().selectedRoleCode || fallbackRoleCode;
    const currentRole = roles.find(role => String(role.role_code || '') === String(persistedRoleCode || ''));
    const resolvedRole = currentRole || roles[0];
    return String(resolvedRole?.role_code || persistedRoleCode || '');
  }

  // Centralize the side effects that must happen whenever header role changes.
  private applySelectedRole(selectedRole: any, businessUnitCode: string, regionName?: string): void {
    this.platformRegionService.selectedRole = selectedRole;
    this.roleStateService.setSelectedRole(selectedRole, businessUnitCode, regionName || null);
  }

  private findAvailableRoleByCode(roleCode: string): any | undefined {
    return this.roleStateService.getSnapshot().availableRoles.find(role => role.role_code === roleCode);
  }

  private handleRoleSelection(selectedRoleDetails: any, businessUnitCode: string, regionName?: string): void {
    if (selectedRoleDetails) {
      this.applySelectedRole(selectedRoleDetails, businessUnitCode, regionName);
      this.intService.setChatStatus({ chat_identifier: 'newchat' });
      this.Router.navigate(['/app-abiomed-chat'], { queryParams: { chat: 'NC' }, skipLocationChange: true }).then(() => {
        this.Router.navigate(['/app-abiomed-chat'], { skipLocationChange: true }).then(() => {
          this.intService.refreshMenuList.next();
        });
      });
      this.toastr.success(`Switched to ${selectedRoleDetails.role_label} Role`)
    } else {
      this.toastr.error(`error occured while switching role`)
    }
  }

  loadRoleDropdownOptions() {
    const { businessUnitCode, regionName } = this.getRoleRequestContext();
    const groupCode = sessionStorage.getItem('roleCode') || '';

    // Persist the current header context before rebuilding the dropdown options.
    this.roleStateService.setContext(businessUnitCode, regionName || null);

    this.chatService.getRoleCodeList(businessUnitCode, regionName).subscribe({
      next: (res: any[]) => {
        if (!Array.isArray(res) || res.length === 0) {
          this.updateDropdownOptions([]);
          return;
        }

        const matchedRoles = this.getMatchedRoles(res, businessUnitCode, regionName);
        this.roleStateService.setAvailableRoles(matchedRoles);
        if (matchedRoles.length === 0) {
          this.updateDropdownOptions([]);
          return;
        }

        // Restore the selected role after navigation or fallback to the first valid option.
        const resolvedRoleCode = this.resolveSelectedRoleCode(matchedRoles, groupCode);
        const options = this.toDropdownOptions(matchedRoles);
        this.updateDropdownOptions(options, resolvedRoleCode);
      },
      error: (error) => {
        console.error('Failed to load role list', error);
        this.updateDropdownOptions([]);
      }
    });
  }

  initializeApimUrl(): void {
    this.keyVaultService.apimUrl$.pipe(take(1)).subscribe(url => this.apimUrl = url);
  }

  loadUserGuideFileName(): Promise<string> {
    return new Promise(resolve => {
      this.traineeService.getCustomizeDetails().subscribe(res => {
        this.userGuideFileName = res?.user_guide;
        resolve(this.userGuideFileName);
      });
    });
  }

  async userGuide(event): Promise<void> {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    this.tts.resetAudio();
    const fileName = await this.loadUserGuideFileName();
    if (!fileName) {
      this.toastr.error('User guide is not available');
      return;
    }
    window.open(fileName, '_blank');
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.checkMobileDevice();
  }

  checkMobileDevice() {
    this.showRole = !(window.innerWidth <= 768);
  }

  updateDropdownOptions(roles, selectedRoleCode?: string) {
    this.dropdownOptions = roles.map(role => ({
      label: role.label,
      value: role.value
    }));

    const roleToSelect = this.dropdownOptions.find(role => role.value === selectedRoleCode)?.value
      || this.dropdownOptions[0]?.value
      || '';

    this.showDropdown = this.shouldShowRoleDropdown(roleToSelect);

    this.menuForm.controls['roleName'].setValue(roleToSelect);
  }

  changeRole(e) {
    // Store the previous role value before any changes
    const previousRoleValue = this.roleStateService.getSnapshot().selectedRoleCode || sessionStorage.getItem('roleCode') || '';

    // Check if there are unsaved changes
    if (this.unsavedChangesService.hasUnsavedChanges()) {
      this.unsavedChangesDialogService.showConfirmation(
        'Do you want to end this Roleplay session?',
        'Switch Role'
      ).subscribe(result => {
        if (result === true) {
          // User confirmed, proceed with role switch
          this.unsavedChangesService.reset();
          this.performRoleSwitch(e);
        } else {
          // User clicked No, revert the dropdown to the previous value
          this.menuForm.controls['roleName'].setValue(previousRoleValue, { emitEvent: false });
        }
      });
      return;
    }

    // No unsaved changes, proceed directly
    this.performRoleSwitch(e);
  }

  private performRoleSwitch(e) {
    const selectedRoleCode = e.value;
    const { businessUnitCode, regionName } = this.getRoleRequestContext();

    const selectedRoleFromState = this.findAvailableRoleByCode(selectedRoleCode);
    if (selectedRoleFromState) {
      this.handleRoleSelection(selectedRoleFromState, businessUnitCode, regionName);
      return;
    }

    this.chatService.getRoleCodeList(businessUnitCode, regionName).subscribe({
      next: (res) => {
        const selectedRoleDetails = res.find(role => role.role_code === selectedRoleCode);
        this.handleRoleSelection(selectedRoleDetails, businessUnitCode, regionName);
      },
      error: (error) => {
        console.error('Error fetching roles:', error);
      }
    });
  }

  menuHandler() {
    this.intService.toggleSidebar();
  }

  logout() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      data: { message: 'Do you want to logout?' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        sessionStorage.clear();
        localStorage.clear();
        this.Router.navigate(['/app-home'], { skipLocationChange: true });
      }
    });
  }

  toggleDropdown(event: MouseEvent) {
    event.stopPropagation();
    this.isDropdownVisible = !this.isDropdownVisible;
    this.isProfileHighlighted = !this.isProfileHighlighted;

    const dropdownContent = document.querySelector('.dropdown-content') as HTMLElement;
    const userImg = document.querySelector('.user-img-lu') as HTMLElement;
    dropdownContent.style.display = this.isDropdownVisible ? 'block' : 'none';
    if (this.isProfileHighlighted) {
      userImg.classList.add('highlighted');
    } else {
      userImg.classList.remove('highlighted');
    }
  }

  closeDropdown(event: MouseEvent) {
    const dropdownContent = document.querySelector('.dropdown-content') as HTMLElement;
    const dropdown = document.querySelector('.dropdown') as HTMLElement;
    if (dropdown && !dropdown.contains(event.target as Node)) {
      this.isDropdownVisible = false;
      dropdownContent.style.display = 'none';
    }
  }

  help(event) {
    this.toggleDropdown(event)
    this.Router.navigate(['/app-help-doc-content'], { skipLocationChange: true });
  }

  ngOnDestroy() {
    document.removeEventListener('click', this.boundCloseDropdown);
  }

  releaseNotes() {
    const confluenceUrl = "https://confluence.jnj.com/spaces/JHUD/pages/1128476545/Business+Release+Notes";
    window.open(confluenceUrl, '_blank');
  }

  replaceSpecialCharacter(text) {
    if (text) {
      const andReplacedWithSpace = text.replaceAll("&", "");
      const commaReplacedWithTilled = andReplacedWithSpace.replaceAll(",", "~");
      const spaceReplacedWithHyphen = commaReplacedWithTilled.replaceAll(" ", "_");
      const modifiedText = spaceReplacedWithHyphen;
      return modifiedText;
    } else {
      this.toastr.error(`Platform Group or Speciality is required`);
    }
  }
}